import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { resolveImage } from '@/lib/image';

export default function Cart() {
    const { cartItems, removeFromCart, updateQuantity, getCartTotal, setIsCartOpen } = useCart();
    const navigate = useNavigate();
    const total = getCartTotal();

    const handleCheckout = () => {
        setIsCartOpen(false);
        navigate('/checkout');
    };

    if (cartItems.length === 0) {
        return (
            <div data-testid="empty-cart" className="flex flex-col items-center justify-center h-[60vh] text-center">
                <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6">
                    <span className="text-4xl">🛒</span>
                </div>
                <p className="font-space text-lg text-slate-400 mb-2">Your cart is empty</p>
                <p className="font-space text-sm text-slate-500">Add some items to get started</p>
            </div>
        );
    }

    return (
        <div data-testid="cart-content" className="flex flex-col h-[calc(100vh-8rem)]">
            <ScrollArea className="flex-1 pr-4">
                <div className="space-y-6 py-6">
                    {cartItems.map((item) => (
                        <div 
                            key={`${item.product_id}-${item.size}`}
                            data-testid={`cart-item-${item.product_id}`}
                            className="flex gap-4"
                        >
                            <div className="w-24 h-24 bg-brand-highlight overflow-hidden flex-shrink-0">
                                <img 
                                    src={resolveImage(item.image_url)} 
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="font-syne font-bold text-sm text-white truncate">
                                    {item.name}
                                </h4>
                                {item.size && (
                                    <p className="font-mono text-xs text-slate-500 mt-1">
                                        SIZE: {item.size}
                                    </p>
                                )}
                                <p className="font-space text-sm text-white mt-2">
                                    ${item.price.toFixed(2)}
                                </p>
                                
                                <div className="flex items-center gap-4 mt-3">
                                    <div className="flex items-center border border-brand-border">
                                        <button
                                            data-testid={`decrease-qty-${item.product_id}`}
                                            onClick={() => updateQuantity(item.product_id, item.size, item.quantity - 1)}
                                            className="p-2 hover:bg-white/5 transition-colors"
                                        >
                                            <Minus className="w-3 h-3 text-slate-400" />
                                        </button>
                                        <span className="w-8 text-center font-mono text-sm text-white">
                                            {item.quantity}
                                        </span>
                                        <button
                                            data-testid={`increase-qty-${item.product_id}`}
                                            onClick={() => updateQuantity(item.product_id, item.size, item.quantity + 1)}
                                            className="p-2 hover:bg-white/5 transition-colors"
                                        >
                                            <Plus className="w-3 h-3 text-slate-400" />
                                        </button>
                                    </div>
                                    <button
                                        data-testid={`remove-item-${item.product_id}`}
                                        onClick={() => removeFromCart(item.product_id, item.size)}
                                        className="p-2 hover:bg-white/5 transition-colors text-slate-500 hover:text-red-400"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </ScrollArea>

            <div className="pt-6 border-t border-brand-border">
                <div className="flex justify-between items-center mb-6">
                    <span className="font-space text-slate-400 uppercase tracking-widest text-sm">Total</span>
                    <span data-testid="cart-total" className="font-syne font-bold text-2xl text-white">
                        ${total.toFixed(2)}
                    </span>
                </div>
                <Button
                    data-testid="checkout-button"
                    onClick={handleCheckout}
                    className="w-full bg-white text-black hover:bg-slate-200 rounded-none py-6 font-bold uppercase tracking-widest"
                >
                    CHECKOUT
                </Button>
            </div>
        </div>
    );
}
